// Routes the Guardian page reads and writes (src/aithora/v3/oversight/guardian-routes.ts,
// src/aithora/router.ts "Emergency response" with v3/oversight/incident-steps.ts, and
// GET /residents). Every route and field here is served (be-care, 2026-09-30).
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

/** GUARDIAN_KINDS in src/aithora/v3/oversight/guardian.ts. */
export type GuardianKind =
  | 'fall'
  | 'abuse_neglect'
  | 'missing_resident'
  | 'medication_error'
  | 'unsafe_feeling'
  | 'distress'
  | 'chest_pain'
  | 'scam'
  | 'other';

/**
 * A step on a signal: claim, going_now, report_filed, closed, and the design's "Confirm
 * suspicion", "Start investigation", the crisis screen and reassessment, "Escalate". A
 * step row may also read 'assigned' (written by POST /guardian/signals/:id/assign).
 */
export type GuardianStep =
  | 'claim'
  | 'going_now'
  | 'report_filed'
  | 'closed'
  | 'confirm_suspicion'
  | 'investigation_started'
  | 'screened'
  | 'reassessed'
  | 'escalated';

/** The reporting clock the server computed (deadlineFor): null deadline = none applies. */
export interface GuardianDeadline {
  deadlineAt: string | null;
  hours: number | null;
  basis: string | null;
  citation: string | null;
  /** Why there is no deadline, when there is none. */
  reason: string | null;
}

export interface GuardianStepRow {
  id: string;
  step: string;
  at: string;
  note: string | null;
  actor: { userId: string | null; name: string | null; role: string | null };
  ledgerEntryId: string | null;
  /** The "LG-" reference of the sealed ledger entry. */
  ledgerRef: string | null;
}

/** One piece of evidence the signal was raised on (the design's ev[]). */
export interface GuardianEvidence {
  at: string;
  /** "Atlas conversation", "Care staff note, Marcus T", "Nurse call system". */
  source: string;
  text: string;
  ledgerRef: string | null;
}

/** One Guardian case as GET /guardian/signals returns it (shapeSignals). */
export interface GuardianSignal {
  id: string;
  caseId: string;
  threadId: string;
  organizationId: string;
  /** Aithora staff only. */
  facilityName?: string | null;
  residentId: string | null;
  kind: GuardianKind;
  source: string;
  surfacedAt: string;
  sbi: boolean | null;
  /** SNF / ALF from the CMS catalog; HOME for an Aithora Home household. */
  facilityType: 'SNF' | 'ALF' | 'HOME' | null;
  owner: { userId: string; name: string | null } | null;
  /** open, claimed, responding, confirmed, reported, investigating, screened, reassessed, closed. */
  stage: string;
  reportedAt: string | null;
  closedAt: string | null;
  refs: string[];
  deadline: GuardianDeadline;
  steps: GuardianStepRow[];
  // ── The building's care team and Aithora only (null for a resident's own read) ──
  residentName?: string | null;
  room?: string | null;
  /** Plain words: what was seen or said. */
  summary?: string | null;
  /** Which suspicion an abuse_neglect / other signal is: abuse, neglect, injury. */
  concern?: 'abuse' | 'neglect' | 'injury' | null;
  evidence?: GuardianEvidence[];
  /**
   * Who a report of this case goes to, from the building's state: the survey agency and
   * law enforcement for a nursing home, the state's APS for assisted living.
   */
  reportTo?: { key: string; label: string; sub: string }[] | null;
  /** True once someone pressed Escalate. */
  escalated?: boolean;
  /** The resident chart's plain label ("Unexplained financial requests"). */
  label?: string | null;
  /** The resident chart's evidence line ("2 mentions in 14d"). */
  note?: string | null;
  /** The resident chart's index of suspicion ("Moderate"). */
  severity?: string | null;
  /**
   * Set when the case is an incident report from the charge nurse inbox (its type, and
   * draft until signed). The full report: GET /guardian/incident-reports (incident-report-routes.ts).
   */
  incidentReport?: { type: string; status: 'draft' | 'signed'; signedAt: string | null } | null;
}

export interface GuardianSignalsResponse {
  real: true;
  scope: { organizationId: string | null };
  count: number;
  signals: GuardianSignal[];
}

/** GET /guardian/signals?open=&limit=: the building's cases (Aithora staff: every building). */
export const guardianSignals = route<GuardianSignalsResponse, { open?: boolean; limit?: number; facilities?: string }>()('GET', `${A}/guardian/signals`, { honest: true });

export interface OpenSignalBody {
  kind: GuardianKind;
  residentId?: string;
  sbi?: boolean | null;
  refs?: string[];
  source?: 'staff' | 'companion' | 'sensor' | 'family';
  /**
   * What the reporter saw, in their words. A staff report (source 'staff') opens with the
   * suspicion confirmed and owned by the reporter, the person who formed it.
   */
  note?: string;
  /** See GuardianSignal.concern (abuse_neglect or other only). */
  concern?: 'abuse' | 'neglect' | 'injury';
}

/** Who was actually told about a new signal ("The care team on shift", a family member, the Aithora care line). */
export interface GuardianTold {
  who: string;
  what: string;
}

/**
 * POST /guardian/signals: "Report a concern", or a resident's or Aithora Home member's own
 * "I don't feel safe". surfaced_at = now starts the clock. `told` lists only who was told.
 */
export const guardianOpenSignal = route<{ real: true; signal: GuardianSignal; ledger: unknown; told: GuardianTold[] }, void, OpenSignalBody>()(
  'POST', `${A}/guardian/signals`, { honest: true });

export interface StepBody {
  step: GuardianStep;
  note?: string;
  /** The serious bodily injury answer given when filing (report_filed only). */
  sbi?: boolean | null;
}

export interface StepResponse {
  real: true;
  stepId: string;
  step: string;
  at: string;
  stage: string;
  ledgerEntryId: string | null;
  ledgerRef: string | null;
}

/** POST /guardian/signals/:id/steps: append-only, each step sealed to the ledger. */
export const guardianStep = route<StepResponse, void, StepBody>()('POST', `${A}/guardian/signals/:id/steps`, { honest: true });

// ── Assignment, watch, tape ─────────────────────────────────────────────────

/** POST /guardian/signals/:id/assign: the Facility Admin names an owner (sealed as an 'assigned' step). */
export const guardianAssign = route<{ real: true; owner: { userId: string; name: string | null } }, void, { ownerUserId: string }>()(
  'POST', `${A}/guardian/signals/:id/assign`, { honest: true });

/** GET /guardian/assignable: the building's care team who may own a case (Facility Admin). */
export const guardianAssignable = route<{ real: true; people: { userId: string; name: string }[] }>()('GET', `${A}/guardian/assignable`, { honest: true });

/** A trend Guardian is watching that has not crossed its line (the design's kind 'watch'). */
export interface GuardianWatchItem {
  id: string;
  organizationId: string;
  facilityName?: string | null;
  facilityType: 'SNF' | 'ALF' | 'HOME' | null;
  residentId: string | null;
  residentName: string | null;
  room: string | null;
  since: string;
  summary: string;
  evidence: GuardianEvidence[];
  /** Who is keeping an eye on it, once someone is. */
  owner?: { userId: string; name: string | null } | null;
}

/**
 * GET /guardian/watch: early-warning trends (not signals yet, no clock): Atlas's
 * below-the-line index-of-suspicion hits per resident and type over 14 days.
 */
export const guardianWatch = route<{ real: true; items: GuardianWatchItem[] }, { facilities?: string }>()('GET', `${A}/guardian/watch`, { honest: true });

/** POST /guardian/watch/:id/assign: someone keeps an eye on a trend (Care Staff: themselves). */
export const guardianWatchAssign = route<{ real: true; owner: { userId: string; name: string | null } }, void, { ownerUserId: string }>()(
  'POST', `${A}/guardian/watch/:id/assign`, { honest: true });

/** One line of the safety tape: every screen, call and note, newest first. */
export interface GuardianTapeEvent {
  at: string;
  /** "Atlas screen", "Emergency", "Nurse call", "Gate", "Care note". */
  kind: string;
  text: string;
}

/** GET /guardian/tape?limit=: the building's safety events as they happen (also "Guardian" for a case opened). */
export const guardianTape = route<{ real: true; events: GuardianTapeEvent[] }, { limit?: number; facilities?: string }>()('GET', `${A}/guardian/tape`, { honest: true });

// ── Emergencies (the design's kind 'emergency') ─────────────────────────────

/** GET /emergency/incidents, the fields Guardian reads (getActiveIncidents). */
export interface GuardianIncident {
  id: string;
  type: string | null;
  status: string;
  created_at: string;
  /** CRITICAL, HIGH or MEDIUM, for the shared severity order (compareEmergencies). */
  severity?: string | null;
  /** 911 is part of the plan. */
  requires_911?: boolean | null;
  /** The resident (ei.user_id). */
  user_id?: string | null;
  organization_id?: string | null;
  description: string | null;
  evidence: string | null;
  acknowledged_at: string | null;
  acknowledgedByUserId?: string | null;
  acknowledgedByName: string | null;
  arrived_at?: string | null;
  arrivedByName?: string | null;
  residentName: string | null;
  room: string | null;
  /** Where it came from ("Atlas conversation"). */
  sourceLabel?: string | null;
  /** The sealed ledger reference of the alert: null today (the alert is not sealed when raised). */
  ledgerRef?: string | null;
  facilityName?: string | null;
  facilityType?: 'SNF' | 'ALF' | 'HOME' | null;
  /** Set once someone recorded "Called 911". */
  called911At?: string | null;
  /** When EMS arrived (AF-1616); paging stops then, and the emergency stays open until closed. */
  emsArrivedAt?: string | null;
  /** Set once someone pressed "Resolved"; a resolved incident stays listed for the week (Closed). */
  resolved_at?: string | null;
  /**
   * Every step taken on it, each sealed to the ledger (going, with them, called 911,
   * resolved), oldest first, with who and the ledger reference.
   */
  steps?: GuardianStepRow[];
}

/**
 * GET /emergency/incidents?recentDays=: the building's open emergencies (staff of the
 * building); with recentDays (Guardian sends 7) also those resolved in that many days.
 */
export const guardianIncidents = route<{ incidents: GuardianIncident[] }, { recentDays?: number }>()('GET', `${A}/emergency/incidents`);

/** GET /emergency/incidents/network: every building's, open and resolved this week, for Aithora staff. */
export const guardianIncidentsNetwork = route<{ incidents: GuardianIncident[] }, { facilities?: string }>()('GET', `${A}/emergency/incidents/network`);

/** POST /emergency/arrived: "I'm with them". */
export const emergencyArrived = route<{ ok: boolean; arrivedAt?: string }, void, { incidentId: string }>()('POST', `${A}/emergency/arrived`);

/** POST /emergency/resolve: "Resolved". */
export const emergencyResolve = route<{ ok: boolean }, void, { incidentId: string; resolution?: string; resolutionType?: 'mitigated' | 'false_positive' }>()(
  'POST', `${A}/emergency/resolve`);

/** POST /emergency/called-911: records that someone called 911 (sealed). */
export const emergencyCalled911 = route<{ ok: boolean; called911At?: string }, void, { incidentId: string }>()('POST', `${A}/emergency/called-911`);
/** POST /emergency/ems-arrived: records that EMS arrived (once) and stops paging; the emergency stays open until closed with what happened (AF-1616). */
export const emergencyEmsArrived = route<{ ok: boolean; emsArrivedAt?: string }, void, { incidentId: string }>()('POST', `${A}/emergency/ems-arrived`);

// ── State reporting rules and agencies ──────────────────────────────────────

export interface GuardianReportTo { key: string; label: string; sub: string }

export interface GuardianStateRulesResponse {
  real: true;
  count: number;
  /** Assisted living reporting clocks per state and kind, each with its citation. */
  rules: { state: string; facilityType: string; kind: string; sbiHours: number | null; standardHours: number | null; citation: string; updatedAt: string | null; reportTo: GuardianReportTo[] | null }[];
  /** Who a report goes to, per state and building type. */
  agencies: { state: string; facilityType: 'SNF' | 'ALF'; reportTo: GuardianReportTo[]; citations: string[]; updatedAt: string | null }[];
  /** The caller's own building (null for Aithora): its state, type and recipients (null = not loaded). */
  building: { state: string | null; facilityType: 'SNF' | 'ALF' | 'HOME' | null; reportTo: GuardianReportTo[] | null } | null;
}

/** GET /guardian/state-rules: reporting clocks and per-state agencies (reportTo). */
export const guardianStateRules = route<GuardianStateRulesResponse>()('GET', `${A}/guardian/state-rules`, { honest: true });

/** PUT /guardian/state-agencies (Aithora only): who a report goes to for one state and building type. */
export const guardianStateAgencies = route<{ real: true; state: string; facilityType: 'SNF' | 'ALF'; citation: string; reportTo: GuardianReportTo[] }, void, { state: string; facilityType: 'SNF' | 'ALF'; citation: string; agencies: GuardianReportTo[] }>()(
  'PUT', `${A}/guardian/state-agencies`, { honest: true });

// ── Residents (for "Report a concern") ──────────────────────────────────────

export interface RosterResident {
  id: string;
  name: string | null;
  preferred_name?: string | null;
}

/** GET /residents: the building's residents (staff only). */
export const residentsRoster = route<{ residents: RosterResident[] }>()('GET', `${A}/residents`);

export const guardianApi = {
  guardianSignals,
  guardianOpenSignal,
  guardianStep,
  guardianAssign,
  guardianAssignable,
  guardianWatch,
  guardianWatchAssign,
  guardianTape,
  guardianIncidents,
  guardianIncidentsNetwork,
  emergencyArrived,
  emergencyResolve,
  emergencyCalled911,
  emergencyEmsArrived,
  guardianStateRules,
  guardianStateAgencies,
  residentsRoster,
} as const;
